import { useEffect, useState } from 'react';
import VisitDate from './VisitDate';
import VisitList from './VisitList';
import { useGetAllAppointmentsApi, useSaveAppointmentsApi } from '@/hooks/api';
import {
  clearVisitDetails,
  selectAdminLoading,
  selectAdminProviderId,
  selectConnectedProviderGuid,
  selectIsProviderAdmin,
  selectProviderState,
  setSelectedVisitDetails,
  useAppDispatch,
  useAppSelector,
  loadLatestNoteStatusSucceeded,
  loadVisitsSucceeded,
  Visit,
  loadVisitsRecordingsSucceeded,
  totalVisitsSelector,
  totalVisitsWithRecordingsCountSelector,
  selectCurrentVisitStatus,
} from '@ax/redux-store';
import { Auth } from '@ax/auth';
import Skeleton from 'react-loading-skeleton';
import { useGetAppointmentsByServiceDateRangeApi } from '@/hooks/api/useGetAppointmentsByServiceDateRange';
import { Button } from '@ax/ui/src/components/Button';
import VisitFormModal from './VisitFormModal';
import { format } from 'date-fns';
import { addDaysToDate, concatenateDateTime } from '@/utils';
import { useGetAllTranscriptFromNoteListApi } from '@/hooks/api/useGetAllTranscriptFromNoteLists';
import { TranscriptRecording } from '@ax/service/src/transcript-interface';
import { NOTE_STATUS } from '@ax/constants';
import { updateAppointmentProperties } from '@ax/service';
import { NoteStatusAppointmentInput } from '@ax/service/src/appointment-interface';
import styles from './Visits.module.scss';
import { recordingState } from '@/store/recordingState.tsx';
import ConfirmationModal from '@/components/ConfirmationModal';

interface VisitsProps {
  readonly?: boolean;
}

const Visits = ({ readonly }: VisitsProps) => {
  const dispatch = useAppDispatch();
  const selectedVisitDate = useAppSelector((state) => state.selectedDate);
  const providerState = useAppSelector(selectProviderState);
  const visits = useAppSelector((state) => state.visits?.items ?? []);
  const [selectedVisit, setSelectedVisit] = useState<string | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [visitSelectedID, setVisitSelectedID] = useState<string>('0');
  const [changeList, setChangeList] = useState<boolean>(false);
  const currentVisitStatus = useAppSelector(selectCurrentVisitStatus(visitSelectedID));
  const [isRecordingInprogress, setIsRecordingInprogress] = useState(
    recordingState.value.recordingInprogress,
  );
  const [pendingVisitItem, setPendingVisitItem] = useState<Visit | undefined>(undefined);
  const [isRecordingInProgressModalOpen, setIsRecordingInProgressModalOpen] = useState(false);
  const [executePendingAction, setExecutePendingAction] = useState<(() => void) | null>(null);

  const handleModalOpen = () => setShowModal(true);
  const handleModalClose = () => setShowModal(false);

  const adminProviderId = useAppSelector(selectAdminProviderId);
  const isProviderAdmin = useAppSelector(selectIsProviderAdmin);
  const doctorId = providerState?.userInfo ? providerState.userInfo.uid : Auth.currentUser()?.uid;
  const providerGuid = useAppSelector(selectConnectedProviderGuid);

  const adminLoading = useAppSelector(selectAdminLoading);
  const totalVisits = useAppSelector(totalVisitsSelector);
  const totalVisitsWithRecordings = useAppSelector(totalVisitsWithRecordingsCountSelector);

  const { data: appointmentList }: any = useGetAppointmentsByServiceDateRangeApi(
    selectedVisitDate.date,
    isProviderAdmin,
    providerGuid,
  );

  const {
    data: visitList,
    isLoading,
    refetch: refetchVisitList,
  }: any = useGetAllAppointmentsApi(
    doctorId,
    selectedVisitDate.date,
    isProviderAdmin,
    adminProviderId,
  );

  const { mutateAsync: saveAppointment } = useSaveAppointmentsApi();
  const { data: appointmentRecordingStatus }: any = useGetAllTranscriptFromNoteListApi(
    visits?.map((visit: Visit) => visit.uuid),
  );

  const visitDataList: Visit[] = visitList?.dataList;

  const createMissingAppointments = async () => {
    const missingAppointments = visitDataList?.filter(
      (visit: any) =>
        !appointmentList?.find(
          (appointmentItem: any) => appointmentItem.appointmentId === visit.uuid,
        ),
    );

    const currentTime = format(new Date(), 'hh:mm a');
    const formattedServiceDate = concatenateDateTime(selectedVisitDate.date, currentTime);

    if (missingAppointments && missingAppointments.length > 0) {
      const newAppointments = missingAppointments.map((visit: any) => {
        return {
          appointmentId: visit?.uuid,
          noteStatus: NOTE_STATUS.NEW_APPOINTMENT,
          serviceDate: formattedServiceDate,
          expirationDate: addDaysToDate(formattedServiceDate, 30),
        };
      });

      await saveAppointment(newAppointments);
    }
  };

  useEffect(() => {
    if (adminLoading) {
      setSelectedVisit(undefined);
      dispatch(clearVisitDetails());
      dispatch(
        loadVisitsSucceeded({
          visits: [],
          date: selectedVisitDate.date,
        }),
      );
    }
  }, [adminLoading]);

  useEffect(() => {
    setIsRecordingInprogress(recordingState.value.recordingInprogress);
    if (!isRecordingInprogress && pendingVisitItem) {
      handleVisitItemClick(pendingVisitItem);
      setPendingVisitItem(undefined);
    }
  }, [recordingState.value.recordingInprogress]);

  useEffect(() => {
    if (selectedVisit && visitDataList && visitDataList.length) {
      const selectedVisitIdx = visitDataList.findIndex((visit) => {
        return visit.uuid === selectedVisit;
      });

      if (selectedVisitIdx > -1) {
        dispatch(setSelectedVisitDetails(visitDataList[selectedVisitIdx]));
      }
    }
  }, [visitDataList, selectedVisit]);

  useEffect(() => {
    const transformedVisitsData = visitDataList?.map((visit: any) => {
      const correspondingAppointment = appointmentList?.find(
        (appointmentItem: any) => appointmentItem.appointmentId === visit.uuid,
      );

      const noteStatus = correspondingAppointment ? correspondingAppointment.noteStatus : undefined;
      const allContentViewed = correspondingAppointment
        ? correspondingAppointment.allContentViewed
        : false;

      if (noteStatus && visit?.uuid) {
        dispatch(loadLatestNoteStatusSucceeded({ status: noteStatus, noteId: visit?.uuid }));
      }

      return {
        uuid: visit.uuid,
        patientFirstName: visit.patientFirstName,
        patientLastName: visit.patientLastName,
        appointmentStartTime: visit.appointmentStartTime,
        patientAge: visit.patientAge,
        patientGender: visit.patientGender,
        chiefComplaint: visit.chiefComplaint,
        roomNumber: visit.roomNumber,
        noteStatus: noteStatus,
        selected: false,
        reminderAvailability: visit.reminderAvailability,
        allContentViewed: allContentViewed,
        source: visit.source,
        deleted: visit.deleted === 'true',
        statusHistory: correspondingAppointment?.noteStatusHistory ?? [],
      };
    });

    if (transformedVisitsData && transformedVisitsData.length > 0) {
      // create missing appointments
      createMissingAppointments().catch(console.error);
    }

    const convertTimeToMinutes = (timeString: any) => {
      const [time, period] = timeString.split(' ');
      const [hour, minute] = time.split(':').map(Number);
      const normalizedHour =
        period === 'AM' ? (hour === 12 ? 0 : hour) : hour === 12 ? 12 : hour + 12;
      return normalizedHour * 60 + minute;
    };

    // sort visits by time
    const sortedVisits = transformedVisitsData?.sort((a, b) => {
      const timeA = convertTimeToMinutes(a.appointmentStartTime);
      const timeB = convertTimeToMinutes(b.appointmentStartTime);
      return timeA - timeB;
    });

    dispatch(
      loadVisitsSucceeded({
        visits: sortedVisits,
        date: selectedVisitDate.date,
      }),
    );
  }, [visitList, appointmentList]);

  useEffect(() => {
    const newVisits: Visit[] = visits.map((v: Visit) => {
      let element = { ...v };
      if (appointmentRecordingStatus) {
        if (element.uuid != undefined) {
          if (
            appointmentRecordingStatus[element.uuid] !== undefined &&
            appointmentRecordingStatus[element.uuid].length > 0
          ) {
            if (appointmentRecordingStatus['' + element.uuid + ''].length > 1) {
              const noteGenerated = appointmentRecordingStatus['' + element.uuid + ''].every(
                (e: TranscriptRecording) => e.noteGenerated,
              );
              if (noteGenerated === false && visitSelectedID === '0') {
                setVisitSelectedID(element?.uuid ? element.uuid : '0');
                setChangeList(true);
                element = {
                  ...element,
                  allContentViewed: element.uuid === selectedVisit,
                };
              } else if (noteGenerated && visitSelectedID != '0') {
                if (element.uuid === visitSelectedID) {
                  element = {
                    ...element,
                  };
                }
              }
            }
          }
        }
      }
      return element;
    });
    if (changeList) {
      const saveValues: NoteStatusAppointmentInput = {
        appointmentId: visitSelectedID,
        noteId: visitSelectedID,
        noteStatus: currentVisitStatus,
        allContentViewed: visitSelectedID === selectedVisit,
      };
      void saveNoteStatus(saveValues);
      setVisitSelectedID('0');
      setChangeList(false);
      dispatch(
        loadLatestNoteStatusSucceeded({ status: currentVisitStatus, noteId: visitSelectedID }),
      );
      dispatch(
        loadVisitsSucceeded({
          visits: newVisits,
          date: selectedVisitDate.date,
        }),
      );
    }

    if (appointmentRecordingStatus) {
      dispatch(loadVisitsRecordingsSucceeded({ stats: appointmentRecordingStatus }));
    }
  }, [appointmentRecordingStatus]);

  useEffect(() => {
    const selectedVisitPresentInList = visits?.find((v: Visit) => v.uuid === selectedVisit);

    if (selectedVisit && !selectedVisitPresentInList && visits?.length > 0) {
      handleVisitItemClick(visits[0]);
    }

    if (selectedVisit && (!visits || visits.length === 0)) {
      setSelectedVisit(undefined);
      dispatch(clearVisitDetails());
    }

    if (!selectedVisit && visits?.length > 0) {
      handleVisitItemClick(visits[0]);
    }
  }, [visits, selectedVisit]);

  const saveNoteStatus = async (values: NoteStatusAppointmentInput) => {
    try {
      await updateAppointmentProperties(values);
    } catch (error) {
      console.error(error);
    }
  };

  const handleVisitItemClick = (visitItem?: Visit) => {
    if (isRecordingInprogress) {
      setPendingVisitItem(visitItem);
      setIsRecordingInProgressModalOpen(true);
      return;
    }

    if (visitItem) {
      if (
        visitItem.allContentViewed === false &&
        visitItem.noteStatus !== NOTE_STATUS.NEW_APPOINTMENT
      ) {
        const saveValues: NoteStatusAppointmentInput = {
          appointmentId: visitItem?.uuid ? visitItem.uuid : '',
          noteId: visitItem?.uuid ? visitItem.uuid : '',
          allContentViewed: true,
        };
        saveNoteStatus(saveValues).catch(console.error);
      }
      setSelectedVisit(visitItem.uuid);
      dispatch(setSelectedVisitDetails(visitItem));
      dispatch(
        loadVisitsSucceeded({
          visits: visits,
          date: selectedVisitDate.date,
        }),
      );
    }
  };

  const handleContinueVisitClick = () => {
    setIsRecordingInProgressModalOpen(false);
    recordingState.value = { recordingInprogress: false };
    setIsRecordingInprogress(false);

    setExecutePendingAction(() => () => {
      if (executePendingAction) {
        executePendingAction();
        setExecutePendingAction(null);
      }
    });
  };

  const handleRecordingInProgress = () => {
    setIsRecordingInProgressModalOpen(true);
  };

  return (
    <>
      {
        <ConfirmationModal
          modalOpen={isRecordingInProgressModalOpen}
          modalConfirm={() => {
            handleContinueVisitClick();
          }}
          modalClose={() => {
            setIsRecordingInProgressModalOpen(false);
          }}
        />
      }
      <div className={styles.visit__list__title_container}>
        <span className={styles.visit__list__title_container__text}>Visits</span>
        {isProviderAdmin && (
          <div className={styles.visit__list__title_container__subtext_container}>
            <span className={styles.visit__list__title_container__subtext_left}>
              <div>With</div>
              <div>recordings</div>
            </span>
            <span className={styles.visit__list__title_container__subtext_right}>
              <span>Total</span>
            </span>
            <span className={styles.visit__list__title_container__subtext_left}>
              <strong>{`${totalVisitsWithRecordings}`}</strong>
            </span>
            <span className={styles.visit__list__title_container__subtext_right}>
              <strong>{`${totalVisits}`}</strong>
            </span>
          </div>
        )}
      </div>
      <div className="mx-[10px] mt-[10px] mb-[10px]">
        <Button
          label="+ Add new"
          className="font-[600]"
          onClick={handleModalOpen}
          small
          outline
          block
          hidden={readonly}
          disabled={readonly}
        />
      </div>
      <VisitDate
        onContinueRecording={handleRecordingInProgress}
        onExecutePendingAction={executePendingAction || (() => {})}
      />
      {isLoading || adminLoading ? (
        <div
          className="pl-[20px] pr-[20px] z-0"
          style={{ position: 'relative' }}
        >
          <Skeleton
            height={12}
            count={3}
          />
        </div>
      ) : (
        <VisitList
          items={visits}
          onVisitItemClick={handleVisitItemClick}
          selectedVisit={selectedVisit}
          readonly={readonly}
        />
      )}
      <VisitFormModal
        modalOpen={showModal}
        modalClose={handleModalClose}
        refetchData={refetchVisitList}
      />
    </>
  );
};

export default Visits;
